import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ChevronLeft, ChevronRight, Activity, Users, UserCheck, UserX, AlertCircle } from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import { secureStorage } from '../../utils/secureStorage';
import { BACKEND_URL } from '../../utils/backendUrl';
import { EMZI_COLORS, EMZI_SHADOWS } from '../../styles/emziCI';

/**
 * EMZI NEXUS — Org Adoption Analytics (admin only)
 *
 * Shows WAU / MAU / Inactive KPIs, a 14-day daily-active sparkline,
 * and the top inactive users (sorted by stalest first, NEVER on top).
 */

const KpiCard = ({ icon: Icon, label, count, sublabel, tint, testId, onClick }) => {
  const interactive = !!onClick;
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      disabled={!interactive}
      style={{
        background: tint.bg,
        border: `1px solid ${tint.border}`,
        borderRadius: 14,
        padding: '14px 16px',
        flex: 1,
        minWidth: 110,
        textAlign: 'left',
        cursor: interactive ? 'pointer' : 'default',
        transition: 'transform 0.12s, box-shadow 0.12s',
        outline: 'none',
        font: 'inherit',
      }}
      onMouseEnter={(e) => {
        if (interactive) {
          e.currentTarget.style.transform = 'translateY(-1px)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
        }
      }}
      onMouseLeave={(e) => {
        if (interactive) {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: tint.label }}>
        <Icon size={14} strokeWidth={2.4} />
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6 }}>{label}</span>
      </div>
      <div style={{ fontSize: 32, fontWeight: 800, lineHeight: 1, marginTop: 6, color: tint.value }}>
        {count}
      </div>
      {sublabel && (
        <div style={{ fontSize: 11, color: tint.sub, marginTop: 4 }}>{sublabel}</div>
      )}
    </button>
  );
};

const DaysBadge = ({ days }) => {
  // NEVER → red; ≥20d → red; ≥15d → pink; else → yellow
  let bg = '#FEF3C7';
  let fg = '#92400E';
  let label = `${days}d`;
  if (days === null || days === undefined) {
    bg = '#DC2626';
    fg = '#FFFFFF';
    label = 'NEVER';
  } else if (days >= 20) {
    bg = '#FECACA';
    fg = '#991B1B';
  } else if (days >= 15) {
    bg = '#FED7E2';
    fg = '#9F1239';
  }
  return (
    <span
      style={{
        background: bg,
        color: fg,
        fontSize: 11,
        fontWeight: 800,
        padding: '4px 10px',
        borderRadius: 999,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
};

const OrgAdoptionPage = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isAdmin()) {
      navigate('/dashboard');
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/admin/org-adoption`, {
          headers: {
            Authorization: `Bearer ${secureStorage.getItem('nexus_access_token')}`,
          },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        setData(json);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [isAdmin, navigate]);

  return (
    <div
      data-testid="org-adoption-page"
      style={{ minHeight: '100vh', background: EMZI_COLORS.background, display: 'flex', flexDirection: 'column' }}
    >
      {/* Header */}
      <div
        style={{
          background: '#002c88',
          padding: '20px',
          color: 'white',
          boxShadow: EMZI_SHADOWS.md,
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            data-testid="org-adoption-back-btn"
            onClick={() => navigate('/admin')}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              padding: 8,
              borderRadius: '50%',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ChevronLeft size={20} color="white" />
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Activity size={22} strokeWidth={2.4} />
              Org Adoption
            </h1>
            {data && (
              <p style={{ fontSize: 12, opacity: 0.9, margin: '4px 0 0 0' }}>
                <span data-testid="org-adoption-summary">
                  {data.wau.count}/{data.total_users} weekly active · {data.inactive.count} inactive flagged
                </span>
              </p>
            )}
          </div>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '16px', maxWidth: 720, width: '100%', margin: '0 auto' }}>
        {loading && (
          <div style={{ padding: 40, textAlign: 'center', color: EMZI_COLORS.textSecondary }}>
            Loading…
          </div>
        )}
        {error && (
          <div
            data-testid="org-adoption-error"
            style={{
              padding: 16,
              borderRadius: 12,
              background: '#FEF2F2',
              border: '1px solid #FCA5A5',
              color: '#991B1B',
              display: 'flex',
              gap: 8,
              alignItems: 'center',
            }}
          >
            <AlertCircle size={18} /> {error}
          </div>
        )}

        {data && (
          <>
            {/* KPIs */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
              <KpiCard
                testId="kpi-wau"
                icon={UserCheck}
                label="WAU"
                count={data.wau.count}
                sublabel={`${data.wau.percentage}% of ${data.total_users}`}
                onClick={data.wau.count > 0 ? () => navigate('/admin/org-adoption/wau') : undefined}
                tint={{
                  bg: '#ECFDF5',
                  border: '#A7F3D0',
                  label: '#047857',
                  value: '#064E3B',
                  sub: '#047857',
                }}
              />
              <KpiCard
                testId="kpi-mau"
                icon={Users}
                label="MAU"
                count={data.mau.count}
                sublabel={`${data.mau.percentage}%`}
                onClick={data.mau.count > 0 ? () => navigate('/admin/org-adoption/mau') : undefined}
                tint={{
                  bg: '#EFF6FF',
                  border: '#BFDBFE',
                  label: '#1D4ED8',
                  value: '#1E3A8A',
                  sub: '#1D4ED8',
                }}
              />
              <KpiCard
                testId="kpi-inactive"
                icon={UserX}
                label="INACTIVE"
                count={data.inactive.count}
                sublabel={data.inactive.count > 0 ? 'tap to view all' : 'flagged'}
                onClick={data.inactive.count > 0 ? () => navigate('/admin/org-adoption/inactive') : undefined}
                tint={{
                  bg: '#FEF2F2',
                  border: '#FECACA',
                  label: '#991B1B',
                  value: '#7F1D1D',
                  sub: '#991B1B',
                }}
              />
            </div>

            {/* Daily Active Chart */}
            <div
              data-testid="daily-active-chart"
              style={{
                background: '#F9FAFB',
                border: `1px solid ${EMZI_COLORS.border}`,
                borderRadius: 14,
                padding: 14,
                marginBottom: 20,
              }}
            >
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.8, color: EMZI_COLORS.textSecondary, marginBottom: 8 }}>
                DAILY ACTIVE (14D)
              </div>
              <ResponsiveContainer width="100%" height={120}>
                <AreaChart data={data.daily_active} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="dailyActiveFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#6366F1" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#6366F1" stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#E5E7EB" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={(v) => v.slice(5)} interval={1} />
                  <YAxis tick={{ fontSize: 10 }} width={24} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ fontSize: 12, borderRadius: 6 }}
                    formatter={(v) => [`${v} users`, 'Active']}
                  />
                  <Area
                    type="monotone"
                    dataKey="count"
                    stroke="#4F46E5"
                    strokeWidth={2}
                    fill="url(#dailyActiveFill)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Top Inactive Users */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, color: EMZI_COLORS.accent }}>
              <AlertCircle size={16} />
              <h3 style={{ fontSize: 12, fontWeight: 800, letterSpacing: 0.6, margin: 0 }}>
                TOP INACTIVE USERS
              </h3>
            </div>

            {data.top_inactive_users.length === 0 ? (
              <div
                data-testid="org-adoption-no-inactive"
                style={{
                  padding: 20,
                  textAlign: 'center',
                  border: `1px dashed ${EMZI_COLORS.border}`,
                  borderRadius: 12,
                  color: EMZI_COLORS.textMuted,
                  fontSize: 13,
                }}
              >
                🎉 Tiada user inactive — semua aktif dalam 7 hari terakhir.
              </div>
            ) : (
              <>
                <div data-testid="inactive-users-list" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {data.top_inactive_users.slice(0, 10).map((u) => (
                    <button
                      key={u.user_id}
                      data-testid={`inactive-user-${u.user_id}`}
                      onClick={() => navigate(`/admin/users?focus=${encodeURIComponent(u.user_id)}`)}
                      style={{
                        background: '#fff',
                        border: `1px solid ${EMZI_COLORS.border}`,
                        borderRadius: 14,
                        padding: '12px 14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'box-shadow 0.15s, transform 0.15s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.boxShadow = EMZI_SHADOWS.sm;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 14,
                            fontWeight: 800,
                            color: EMZI_COLORS.textPrimary,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {u.name}
                        </div>
                        <div
                          style={{
                            fontSize: 11,
                            color: EMZI_COLORS.textSecondary,
                            marginTop: 2,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {u.role} · {u.group}
                        </div>
                      </div>
                      <DaysBadge days={u.days_inactive} />
                      <ChevronRight size={16} color={EMZI_COLORS.textMuted} />
                    </button>
                  ))}
                </div>

                {data.top_inactive_users.length > 10 && (
                  <button
                    data-testid="view-all-inactive-btn"
                    onClick={() => navigate('/admin/org-adoption/inactive')}
                    style={{
                      marginTop: 10,
                      width: '100%',
                      background: '#fff',
                      border: `1.5px dashed ${EMZI_COLORS.accent}`,
                      borderRadius: 12,
                      padding: '10px 14px',
                      fontSize: 13,
                      fontWeight: 700,
                      color: EMZI_COLORS.accent,
                      cursor: 'pointer',
                    }}
                  >
                    View all {data.top_inactive_users.length} inactive users by team →
                  </button>
                )}
              </>
            )}

            <div style={{ textAlign: 'right', fontSize: 10, color: EMZI_COLORS.textMuted, marginTop: 14 }}>
              Generated: {new Date(data.generated_at).toLocaleString()}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default OrgAdoptionPage;
